import {IFormViewControl, IFormViewSection, IFormViewTab} from '../useFormView';
import {APIActionStatus, Column, Form, formsStore} from '../../../../../stores/Form';
import {callLinkedActions} from '../../../actions/screen';

export const getControlField = (c?: IFormViewControl | null) =>
    c?.Column ? {Column: c.Column} : null;

export const isControlAvailable = (control?: IFormViewControl, skipDisabled = true): boolean => {
    if (skipDisabled) {
        return !!control?.Column && !control.Column.isReadonly;
    }
    return !!control?.Column;
};

export const getCursorPosition = (tabs: IFormViewTab[], columnId: number) => {
    for (let tabIndex = 0; tabIndex < tabs.length; tabIndex++) {
        const tab = tabs[tabIndex];
        for (let sectionIndex = 0; sectionIndex < tab.Sections.length; sectionIndex++) {
            const rows = tab.Sections[sectionIndex].Rows;
            for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
                const row = rows[rowIndex];
                for (let columnIndex = 0; columnIndex < row.length; columnIndex++) {
                    if (row[columnIndex].Column.id === columnId) {
                        return {sectionIndex, rowIndex, columnIndex, tabId: tab.Id};
                    }
                }
            }
        }
    }
    return null;
};

export function findInRow(
    row: IFormViewControl[] | undefined,
    startIndex: number = 0,
    skipDisabled = true
): Column | undefined {
    if (!row) return null;

    for (let i = startIndex; i < row.length; i++) {
        const control = row[i];
        if (isControlAvailable(control, skipDisabled)) return control.Column;
    }

    return null;
}

export const findInRowReverse = (
    row: IFormViewControl[] | undefined,
    fromIndex: number,
    skipDisabled = true
): Column | null => {
    if (!row) return null;
    for (let i = fromIndex - 1; i >= 0; i--) {
        const control = row[i];
        if (isControlAvailable(control, skipDisabled)) return control.Column;
    }
    return null;
};

export const findInPrevRows = (
    rows: IFormViewControl[][],
    fromRowIndex: number,
    skipDisabled?: boolean
): Column | undefined => {
    for (let r = fromRowIndex - 1; r >= 0; r--) {
        const row = rows[r];
        if (!row) continue;
        for (let i = row.length - 1; i >= 0; i--) {
            const control = row[i];
            if (isControlAvailable(control, skipDisabled)) return control.Column;
        }
    }
    return null;
};

export const searchInTab = (
    tab: IFormViewTab,
    tabId: string,
    startSection: number,
    startRow: number,
    startColumn: number,
    isCurrentTab: boolean
): {Column: Column; ActiveTab: string} | undefined => {
    for (let s = startSection; s < tab.Sections.length; s++) {
        const section = tab.Sections[s];
        for (
            let r = isCurrentTab && s === startSection ? startRow : 0;
            r < section.Rows.length;
            r++
        ) {
            const row = section.Rows[r];
            if (!row || row.length === 0) continue;
            const columnStartIndex =
                isCurrentTab && s === startSection && r === startRow ? startColumn + 1 : 0;

            const column = findInRow(row, columnStartIndex, true);

            if (column) return {Column: column, ActiveTab: tabId};
        }
    }
    return undefined;
};

export const searchInTabReverse = (
    tab: IFormViewTab,
    startSection: number,
    startRow: number,
    startColumn: number,
    isCurrentTab: boolean
): {Column: Column; ActiveTab: string} | null => {
    for (let s = startSection; s >= 0; s--) {
        const section = tab.Sections[s];
        const startRowIndex =
            isCurrentTab && s === startSection ? startRow : section.Rows.length - 1;

        for (let r = startRowIndex; r >= 0; r--) {
            const row = section.Rows[r];
            if (!row || row.length === 0) continue;
            const startColumnIndex =
                isCurrentTab && s === startSection && r === startRow ? startColumn : row.length; // the first index to check the available control in the current position row, the second in the the sibling row
            const column = findInRowReverse(row, startColumnIndex, true);

            if (column) {
                return {Column: column, ActiveTab: tab.Id};
            }
        }
    }
    return null;
};

export const findSiblingInPrevSections = (
    sections: IFormViewSection[],
    fromSectionIndex: number,
    rowIndex: number,
    skipDisabled?: boolean
): Column | undefined => {
    for (let s = fromSectionIndex - 1; s >= 0; s--) {
        const section = sections[s];
        const sameRow = section.Rows?.[rowIndex];
        const sameRowControl = findInRowReverse(sameRow, sameRow?.length ?? 0, skipDisabled);
        if (sameRowControl) return sameRowControl;

        const foundInPrev = findInPrevRows(section.Rows, rowIndex, skipDisabled);
        if (foundInPrev) return foundInPrev;
    }
    return undefined;
};

export const findSiblingInNextSections = (
    sections: IFormViewSection[],
    fromSection: number,
    rowIndex: number,
    skipDisabled?: boolean
): Column | undefined => {
    for (let s = fromSection + 1; s < sections.length; s++) {
        const section = sections[s];
        const sameRow = section.Rows[rowIndex];
        const control = findInRow(sameRow, 0, skipDisabled);
        if (control) return control;

        for (let r = rowIndex - 1; r >= 0; r--) {
            const prevRow = section.Rows[r];
            const found = findInRow(prevRow, 0, skipDisabled);
            if (found) return found;
        }
    }
    return undefined;
};

export function findControlInVerticalRows(
    rows: (IFormViewControl[] | undefined)[],
    columnIndex: number,
    direction: 'up' | 'down'
): {Column: Column} | null {
    const rowIndices =
        direction === 'up'
            ? [...Array(rows.length).keys()].reverse()
            : [...Array(rows.length).keys()];

    for (const r of rowIndices) {
        const row = rows[r];
        if (!row || row.length === 0) continue;

        // Rule 1–2: Try to find control at columnIndex or to the left
        for (let c = columnIndex; c >= 0; c--) {
            const result = getControlField(row[c]);
            if (result) return result;
        }

        // Rule 3: Try any available control in the row
        for (const control of row) {
            const result = getControlField(control);
            if (result) return result;
        }
    }
    return null;
}

export const findInNextTabs = (
    tabs: IFormViewTab[],
    fromTabIndex: number,
    rowIndex: number,
    skipDisabled?: boolean
): {Column: Column; ActiveTab: string} | undefined => {
    for (let t = fromTabIndex + 1; t < tabs.length; t++) {
        const tab = tabs[t];
        const section = tab.Sections[0];
        if (!section) continue;

        const sameRow = section.Rows[rowIndex];
        const control = findInRow(sameRow, 0, skipDisabled);
        if (control) return {Column: control, ActiveTab: tab.Id};

        for (let r = rowIndex - 1; r >= 0; r--) {
            const prevRow = section.Rows[r];
            const found = findInRow(prevRow, 0, skipDisabled);
            if (found) return {Column: found, ActiveTab: tab.Id};
        }
    }
    return undefined;
};

export const findInPrevTabs = (
    tabs: IFormViewTab[],
    currentTabIndex: number,
    rowIndex: number,
    skipDisabled?: boolean
): {Column: Column; ActiveTab: string} | null => {
    for (let t = currentTabIndex - 1; t >= 0; t--) {
        const tab = tabs[t];
        for (let s = tab.Sections.length - 1; s >= 0; s--) {
            const section = tab.Sections[s];
            const sameRow = section.Rows?.[rowIndex];
            const sameRowControl = findInRowReverse(sameRow, sameRow?.length || 0, skipDisabled);
            if (sameRowControl) return {Column: sameRowControl, ActiveTab: tab.Id};

            const foundInPrev = findInPrevRows(section.Rows, rowIndex, skipDisabled);
            if (foundInPrev) return {Column: foundInPrev, ActiveTab: tab.Id};
        }
    }
    return null;
};

export const changeFormRow = (newIndex: number, form: Form) => {
    callLinkedActions(formsStore.currentScreen.checkAndSave, () => {
        form.setCurrentRow(newIndex);
        return APIActionStatus.passed;
    });
};
